import { onMounted, onUnmounted } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { useUploadStore } from '../stores/useUploadStore'
import { assetKeys, workspaceKeys } from '../queries'

/**
 * Refreshes what an upload landing changes, for as long as the component that calls it stays
 * mounted — the application layout, so the whole session.
 *
 * Two families of reads: the library's (`assetKeys.all` — every list and search, and the asset
 * itself), and the workspace reads, which carry the byte and asset counts an upload moves.
 *
 * @example
 * ```vue
 * <script setup>
 * import { useUploadCacheInvalidation } from '@/apps/assets/composables/useUploadCacheInvalidation'
 *
 * useUploadCacheInvalidation()
 * </script>
 * ```
 */
export function useUploadCacheInvalidation(): void {
  const queryClient = useQueryClient()
  const uploadStore = useUploadStore()

  let unsubscribe: (() => void) | null = null

  onMounted(() => {
    unsubscribe = uploadStore.onUploadComplete(() => {
      void queryClient.invalidateQueries({ queryKey: assetKeys.all })
      void queryClient.invalidateQueries({ queryKey: workspaceKeys.all })
    })
  })

  onUnmounted(() => {
    unsubscribe?.()
  })
}
